/**
 * All SQL helpers that aren't a one-liner live here.
 *
 * Convention: functions return plain data (no Supabase response objects).
 * Errors throw — callers handle. Aggregations that should be SQL views
 * eventually start as JS-side reductions while data volumes are small.
 */

import { supabase } from './supabase.js';
import { aggregatePitchStatsByAthlete, countBy } from './aggregation.js';
import { formatSessionLabel } from '../ui/components/session-label.js';

/**
 * Active athletes with derived per-athlete stats merged in.
 *
 * Stats are aggregated client-side from rapsodo_pitches (cheap at MVP scale —
 * dozens of athletes × hundreds of pitches each = a few thousand rows). Once
 * we hit five-figure pitch counts, push these into a Postgres view called
 * `athletes_with_stats` and just `select * from` that view.
 *
 * Returns rows with shape:
 *   { id, first_name, last_name, nickname, dob, throws_hand, is_active,
 *     joined_date, age, last_training_date, total_pitches, total_uploads,
 *     peak_velo_mph, primary_pitch_type, intent_summary }
 */
export async function getAthletesWithStats() {
  const [athletesRes, pitchesRes, uploadsRes] = await Promise.all([
    supabase
      .from('athletes')
      .select('*')
      .eq('is_active', true)
      .order('last_name', { ascending: true }),
    supabase
      .from('rapsodo_pitches')
      .select('athlete_id, thrown_at, rel_speed_mph, intent_type, pitch_type'),
    supabase
      .from('rapsodo_uploads')
      .select('athlete_id'),
  ]);

  if (athletesRes.error) throw athletesRes.error;
  if (pitchesRes.error) throw pitchesRes.error;
  if (uploadsRes.error) throw uploadsRes.error;

  const stats = aggregatePitchStatsByAthlete(pitchesRes.data ?? []);
  const uploadCounts = countBy(uploadsRes.data ?? [], 'athlete_id');

  return (athletesRes.data ?? []).map((a) => {
    const s = stats[a.id] ?? null;
    return {
      ...a,
      // age remains for backward-compat with any UI still reading it,
      // but athlete_age (manually entered) is the source of truth now.
      age: a.athlete_age ?? ageFromDob(a.dob),
      last_training_date: s?.lastThrownAt ?? null,
      total_pitches: s?.count ?? 0,
      total_uploads: uploadCounts[a.id] ?? 0,
      peak_velo_mph: s?.peakVelo ?? null,
      primary_pitch_type: s?.primaryPitchType ?? null,
      intent_summary: s?.intentSummary ?? null,
    };
  });
}

/**
 * Most recent N rapsodo uploads, joined with athlete name.
 * For the home page sidebar / activity feed.
 */
export async function getRecentUploads(limit = 5) {
  const { data, error } = await supabase
    .from('rapsodo_uploads')
    .select(`
      id, rapsodo_session_id, title, uploaded_at, row_count,
      athletes ( id, first_name, last_name )
    `)
    .order('uploaded_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

/**
 * One athlete by id, with the same derived stats getAthletesWithStats() returns.
 * Returns null if no athlete with that id (or RLS blocks the read).
 */
export async function getAthleteById(id) {
  const [athleteRes, pitchesRes, uploadsRes] = await Promise.all([
    supabase.from('athletes').select('*').eq('id', id).maybeSingle(),
    supabase
      .from('rapsodo_pitches')
      .select('athlete_id, thrown_at, rel_speed_mph, intent_type, pitch_type')
      .eq('athlete_id', id),
    supabase.from('rapsodo_uploads').select('athlete_id').eq('athlete_id', id),
  ]);

  if (athleteRes.error) throw athleteRes.error;
  if (!athleteRes.data) return null;
  if (pitchesRes.error) throw pitchesRes.error;
  if (uploadsRes.error) throw uploadsRes.error;

  const stats = aggregatePitchStatsByAthlete(pitchesRes.data ?? [])[id] ?? null;

  return {
    ...athleteRes.data,
    age: athleteRes.data.athlete_age ?? ageFromDob(athleteRes.data.dob),
    last_training_date: stats?.lastThrownAt ?? null,
    total_pitches: stats?.count ?? 0,
    total_uploads: (uploadsRes.data ?? []).length,
    peak_velo_mph: stats?.peakVelo ?? null,
    primary_pitch_type: stats?.primaryPitchType ?? null,
    intent_summary: stats?.intentSummary ?? null,
  };
}

/**
 * Every pitch this athlete has thrown. Sorted by thrown_at ascending.
 * Returns the full pitch row — caller picks fields to chart.
 */
export async function getPitchesByAthlete(id) {
  const { data, error } = await supabase
    .from('rapsodo_pitches')
    .select('*')
    .eq('athlete_id', id)
    .order('thrown_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Every Pocket Radar throw this athlete has thrown. Sorted by thrown_at ascending.
 * Used by the Pocket Radar trend chart on /athletes/:id.
 */
export async function getPocketRadarThrowsByAthlete(id) {
  const { data, error } = await supabase
    .from('pocket_radar_throws')
    .select('*')
    .eq('athlete_id', id)
    .order('thrown_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Rapsodo upload list for an athlete, used by the session picker on the
 * dashboards tab. Each row becomes one item in the dropdown.
 *
 * Returns rows ordered DESC by `started_at` (the time of the first pitch
 * in the session) — falls back to `uploaded_at` for empty uploads.
 *
 * Shape: { id, label, count, started_at, title }
 *   id         — rapsodo_uploads.id (used as upload_id filter on pitches)
 *   label      — display string for the dropdown ("Apr 28 — bullpen (15)")
 *   count      — pitch count
 *   started_at — ISO timestamp of first pitch
 *   title      — raw rapsodo_uploads.title (kept for caller flexibility)
 */
export async function getRapsodoUploadsByAthlete(athleteId) {
  const [uploadsRes, pitchesRes] = await Promise.all([
    supabase
      .from('rapsodo_uploads')
      .select('id, title, uploaded_at, row_count')
      .eq('athlete_id', athleteId),
    supabase
      .from('rapsodo_pitches')
      .select('upload_id, thrown_at')
      .eq('athlete_id', athleteId),
  ]);
  if (uploadsRes.error) throw uploadsRes.error;
  if (pitchesRes.error) throw pitchesRes.error;

  const minThrownByUpload = minByGroup(pitchesRes.data ?? [], 'upload_id', 'thrown_at');
  const sessions = (uploadsRes.data ?? []).map((u) => ({
    id: u.id,
    title: u.title ?? null,
    count: u.row_count ?? 0,
    started_at: minThrownByUpload[u.id] ?? u.uploaded_at,
    uploaded_at: u.uploaded_at,
  }));
  sessions.sort((a, b) => (b.started_at ?? '').localeCompare(a.started_at ?? ''));
  return assignSessionLabels(sessions);
}

/**
 * Pocket Radar upload list for an athlete. Same shape and ordering as
 * getRapsodoUploadsByAthlete. The label uses the session_title from the
 * coach-typed PR iPad name when present.
 */
export async function getPocketRadarUploadsByAthlete(athleteId) {
  const [uploadsRes, throwsRes] = await Promise.all([
    supabase
      .from('pocket_radar_uploads')
      .select('id, session_title, source_filename, uploaded_at, row_count')
      .eq('athlete_id', athleteId),
    supabase
      .from('pocket_radar_throws')
      .select('upload_id, thrown_at')
      .eq('athlete_id', athleteId),
  ]);
  if (uploadsRes.error) throw uploadsRes.error;
  if (throwsRes.error) throw throwsRes.error;

  const minThrownByUpload = minByGroup(throwsRes.data ?? [], 'upload_id', 'thrown_at');
  const sessions = (uploadsRes.data ?? []).map((u) => ({
    id: u.id,
    title: u.session_title ?? null,
    count: u.row_count ?? 0,
    started_at: minThrownByUpload[u.id] ?? u.uploaded_at,
    uploaded_at: u.uploaded_at,
    source_filename: u.source_filename ?? null,
  }));
  sessions.sort((a, b) => (b.started_at ?? '').localeCompare(a.started_at ?? ''));
  return assignSessionLabels(sessions, { unitSingular: 'throw', unitPlural: 'throws' });
}

/**
 * Every artifact (Rapsodo CSV/PDF, Pocket Radar CSV, paper assessment scan,
 * photo, video, note, etc.) attached to this athlete. Sorted by session_date
 * descending so the most recent training day is on top. Used by the Files
 * tab on /athletes/:id.
 */
export async function getAthleteFilesByAthlete(id) {
  const { data, error } = await supabase
    .from('athlete_files')
    .select('*')
    .eq('athlete_id', id)
    .order('session_date', { ascending: false })
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/**
 * Generate a signed URL for downloading / previewing a file from the
 * athlete-files Storage bucket. TTL = 1 hour. Returns null if the
 * storage_path is missing (legacy rows where the file was not preserved).
 */
export async function createAthleteFileSignedUrl(storagePath, expiresInSeconds = 3600) {
  if (!storagePath) return null;
  const { data, error } = await supabase
    .storage
    .from('athlete-files')
    .createSignedUrl(storagePath, expiresInSeconds);
  if (error) throw error;
  return data?.signedUrl ?? null;
}

/**
 * Bulk-insert multiple athletes from a parsed CSV. Used by the bulk
 * import UI on the home page. Inserts per-row (rather than a single
 * batch insert) so a UNIQUE collision on one row doesn't kill the whole
 * batch — each row is reported as added, duplicate, or error.
 *
 * Returns { added, duplicates, errors } where each is an array of
 * { row, athlete? | error? } objects for UI rendering.
 */
export async function bulkInsertAthletes(rows) {
  const result = { added: [], duplicates: [], errors: [] };

  for (const row of rows) {
    if (!row.first_name || !row.last_name) {
      result.errors.push({ row, error: 'first_name and last_name are required' });
      continue;
    }

    const payload = {
      first_name: String(row.first_name).trim(),
      last_name: String(row.last_name).trim(),
      athlete_age: row.athlete_age != null && row.athlete_age !== '' ? Number(row.athlete_age) : null,
      playing_age: row.playing_age?.trim() || null,
      dob: row.dob || null,
      throws_hand: row.throws_hand || 'R',
      nickname: row.nickname?.trim() || null,
      joined_date: row.joined_date || new Date().toISOString().slice(0, 10),
      data_incomplete: false,
      is_active: true,
    };

    const { data, error } = await supabase
      .from('athletes')
      .insert(payload)
      .select()
      .single();

    if (error) {
      // Postgres unique-violation code = 23505. We surface as 'duplicate'
      // so the UI can highlight it differently from a true error.
      if (error.code === '23505' || /duplicate key/i.test(error.message ?? '')) {
        result.duplicates.push({ row });
      } else {
        result.errors.push({ row, error: error.message });
      }
      continue;
    }

    result.added.push({ row, athlete: data });
  }

  return result;
}

/**
 * Insert a new athlete row. Used by:
 *   - the prominent "+ Add Pitcher" button on the home page
 *   - the inline create-athlete flow when an upload's pitcher name
 *     doesn't match any existing athlete
 *
 * Required: first_name, last_name. Everything else is optional.
 *
 * Age model: pass either athlete_age (integer years) or playing_age
 * (text like "13U") or both. DOB is supported as legacy/optional metadata
 * but no longer drives anything in the UI.
 *
 * Returns the inserted row (with generated id). Throws on UNIQUE
 * collision — caller surfaces a friendly message.
 */
export async function createAthlete({
  first_name,
  last_name,
  athlete_age = null,
  playing_age = null,
  dob = null,
  throws_hand = 'R',
  nickname = null,
  joined_date = null,
}) {
  const row = {
    first_name: first_name?.trim(),
    last_name: last_name?.trim(),
    athlete_age: athlete_age != null && athlete_age !== '' ? Number(athlete_age) : null,
    playing_age: playing_age?.trim() || null,
    dob: dob || null,
    throws_hand: throws_hand || 'R',
    nickname: nickname?.trim() || null,
    joined_date: joined_date || new Date().toISOString().slice(0, 10),
    data_incomplete: false,
    is_active: true,
  };
  if (!row.first_name || !row.last_name) {
    throw new Error('First name and last name are required.');
  }
  const { data, error } = await supabase
    .from('athletes')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Update an existing athlete row. Used by the Edit form on the Profile tab.
 * Only fields explicitly passed in are updated — unspecified fields are
 * left alone. Pass null to clear a nullable field (e.g. clear nickname).
 *
 * Returns the updated row. Throws on UNIQUE collision (e.g. renaming to
 * collide with an existing athlete) — caller surfaces a friendly message.
 */
export async function updateAthlete(id, fields) {
  if (!id) throw new Error('Athlete id required');

  const patch = {};
  if ('first_name'  in fields) patch.first_name  = fields.first_name?.trim()  || null;
  if ('last_name'   in fields) patch.last_name   = fields.last_name?.trim()   || null;
  if ('nickname'    in fields) patch.nickname    = fields.nickname?.trim()    || null;
  if ('throws_hand' in fields) patch.throws_hand = fields.throws_hand || 'R';
  if ('athlete_age' in fields) {
    patch.athlete_age = fields.athlete_age != null && fields.athlete_age !== ''
      ? Number(fields.athlete_age)
      : null;
  }
  if ('playing_age' in fields) patch.playing_age = fields.playing_age?.trim() || null;
  if ('dob'         in fields) patch.dob         = fields.dob || null;
  if ('is_active'   in fields) patch.is_active   = !!fields.is_active;
  patch.updated_at = new Date().toISOString();

  // Sanity: required fields can't be cleared.
  if ('first_name' in fields && !patch.first_name) {
    throw new Error('First name is required.');
  }
  if ('last_name' in fields && !patch.last_name) {
    throw new Error('Last name is required.');
  }

  const { data, error } = await supabase
    .from('athletes')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Delete a Rapsodo session and everything attached to it: pitches
 * (cascades via FK), the upload row, the athlete_files archive row,
 * and the original CSV in Storage. Best-effort on the storage + files
 * cleanup — if those are missing for a legacy upload, the upload row
 * is still deleted.
 *
 * Used by the delete-row button on the sessions table when a coach
 * needs to drop bad data (e.g. machine was facing the wrong direction).
 */
export async function deleteRapsodoSession(uploadId) {
  if (!uploadId) throw new Error('uploadId is required');

  // Look up the upload first so we can find the linked athlete_files
  // row and storage object before cascading deletes wipe the link.
  const { data: upload, error: lookupError } = await supabase
    .from('rapsodo_uploads')
    .select('id, athlete_id, uploaded_at, file_path')
    .eq('id', uploadId)
    .maybeSingle();
  if (lookupError) throw lookupError;
  if (!upload) throw new Error('Session not found');

  // Delete the storage object (if the file was preserved).
  if (upload.file_path) {
    try {
      await supabase.storage.from('athlete-files').remove([upload.file_path]);
    } catch (err) {
      console.warn('[deleteRapsodoSession] storage remove failed:', err);
    }
  }

  // Delete the athlete_files row. Two link strategies:
  //   1. By storage_path (preferred — exact match for files uploaded post-Storage wiring)
  //   2. By (athlete_id, uploaded_at, source) — for legacy backfill rows (storage_path NULL)
  try {
    if (upload.file_path) {
      await supabase.from('athlete_files').delete().eq('storage_path', upload.file_path);
    } else {
      await supabase
        .from('athlete_files')
        .delete()
        .eq('athlete_id', upload.athlete_id)
        .eq('source', 'rapsodo_csv')
        .eq('uploaded_at', upload.uploaded_at);
    }
  } catch (err) {
    console.warn('[deleteRapsodoSession] athlete_files cleanup failed:', err);
  }

  // Finally delete the upload — pitches CASCADE via FK.
  const { error: deleteError } = await supabase
    .from('rapsodo_uploads')
    .delete()
    .eq('id', uploadId);
  if (deleteError) throw deleteError;

  return { uploadId, athleteId: upload.athlete_id };
}

/**
 * Delete a Pocket Radar session and everything attached: throws
 * (cascades), the upload row, the athlete_files row, the CSV in
 * Storage. Same shape as deleteRapsodoSession.
 */
export async function deletePocketRadarSession(uploadId) {
  if (!uploadId) throw new Error('uploadId is required');

  const { data: upload, error: lookupError } = await supabase
    .from('pocket_radar_uploads')
    .select('id, athlete_id, uploaded_at, file_path')
    .eq('id', uploadId)
    .maybeSingle();
  if (lookupError) throw lookupError;
  if (!upload) throw new Error('Session not found');

  if (upload.file_path) {
    try {
      await supabase.storage.from('athlete-files').remove([upload.file_path]);
    } catch (err) {
      console.warn('[deletePocketRadarSession] storage remove failed:', err);
    }
  }

  try {
    if (upload.file_path) {
      await supabase.from('athlete_files').delete().eq('storage_path', upload.file_path);
    } else {
      await supabase
        .from('athlete_files')
        .delete()
        .eq('athlete_id', upload.athlete_id)
        .eq('source', 'pocket_radar_csv')
        .eq('uploaded_at', upload.uploaded_at);
    }
  } catch (err) {
    console.warn('[deletePocketRadarSession] athlete_files cleanup failed:', err);
  }

  const { error: deleteError } = await supabase
    .from('pocket_radar_uploads')
    .delete()
    .eq('id', uploadId);
  if (deleteError) throw deleteError;

  return { uploadId, athleteId: upload.athlete_id };
}

/**
 * Per-key minimum of a string field across an array of rows. Used to
 * find a session's actual start time (MIN(thrown_at) per upload_id).
 * Returns { [key]: minValue } — string-comparable.
 */
function minByGroup(rows, keyField, valueField) {
  const out = {};
  for (const row of rows) {
    const k = row[keyField];
    const v = row[valueField];
    if (k == null || v == null) continue;
    if (out[k] == null || v < out[k]) out[k] = v;
  }
  return out;
}

/**
 * Generate display labels for a sorted session list, disambiguating
 * same-day duplicates with " #2", " #3", etc. (in chronological order
 * within the day, so "#1" is the earliest). The caller must pass the
 * sessions sorted DESC by `started_at` — labels are computed in chrono
 * order then re-attached to the DESC-sorted output.
 */
function assignSessionLabels(sessions, options = {}) {
  const chrono = [...sessions].sort((a, b) => (a.started_at ?? '').localeCompare(b.started_at ?? ''));
  const dayCounts = {};
  const dayIndex = {};
  for (const s of chrono) {
    const day = (s.started_at ?? '').slice(0, 10);
    dayCounts[day] = (dayCounts[day] ?? 0) + 1;
  }
  const labelById = {};
  for (const s of chrono) {
    const day = (s.started_at ?? '').slice(0, 10);
    dayIndex[day] = (dayIndex[day] ?? 0) + 1;
    const dupIndex = dayCounts[day] > 1 ? dayIndex[day] : null;
    labelById[s.id] = formatSessionLabel({
      startedAt: s.started_at,
      title: s.title,
      count: s.count,
      dupIndex,
      unitSingular: options.unitSingular ?? 'pitch',
      unitPlural: options.unitPlural ?? 'pitches',
    });
  }
  return sessions.map((s) => ({ ...s, label: labelById[s.id] }));
}

function ageFromDob(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}
